import DOMPurify from "dompurify";
import { assetUrl, BOKEH_FALLBACK } from "@/lib/api";
import { Page } from "@/lib/types";
import { styleVars, textToHtml } from "@/lib/pageStyle";
import { PricingTable } from "./PricingTable";

export const MOCKUP_STYLE_LABELS: Record<string, string> = { classic_warm_white: "Classic warm white", bright_white: "Bright white", multicolor_festive: "Multicolor festive", candy_cane: "Candy cane red & white" };
export const mockupLabel = (m: any) => `${m.source === "ai" ? MOCKUP_STYLE_LABELS[m.style] || m.style : "Custom design"}${m.placement ? ` · ${m.placement === "below_gutter" ? "below gutter" : "above gutter"}` : ""}`;

interface Props { page: Page; quote: any; variant: "paper" | "flow" }

const Eyebrow = ({ children, color }: { children: React.ReactNode; color?: string }) => (
  <div className="mb-[1em] text-[0.6em] font-semibold uppercase tracking-[0.3em]" style={{ color: color || "var(--accent)" }}>{children}</div>
);

const Body = ({ page, className = "" }: { page: Page; className?: string }) => {
  const html = page.body_html || textToHtml(page.body);
  if (!html) return null;
  const safe = DOMPurify.sanitize(html, { ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "s", "ul", "ol", "li", "h3", "blockquote", "span", "a"], ALLOWED_ATTR: ["style", "href", "target", "rel"], ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i });
  return <div className={`rich ${className}`} style={{ color: "var(--page-text)", opacity: 0.85 }} dangerouslySetInnerHTML={{ __html: safe }} />;
};

export function ProposalPage({ page, quote, variant }: Props) {
  const paper = variant === "paper";
  const vars = styleVars(page.style || {});
  const wrap = paper ? "paper w-full overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.18)] ring-1 ring-black/5" : "w-full";
  const pad = paper ? "p-[8%]" : "px-6 py-20 md:px-16 lg:px-28";
  const inner = paper ? "paper-inner" : "mx-auto max-w-6xl text-[16px] md:text-[18px]";
  const fb: Record<string, string | null> = quote.fallbacks || {};
  const img = (u?: string | null, slot?: string) => assetUrl(u) || (slot && assetUrl(fb[slot])) || BOKEH_FALLBACK;
  const pageStyle = { ...vars, background: "var(--page-bg)", color: "var(--page-text)" } as React.CSSProperties;
  const H = ({ children, className = "" }: any) => <h2 className={`font-serif-display ${className}`} style={{ fontFamily: "var(--heading-font)", color: "var(--page-text)" }}>{children}</h2>;
  const Sub = ({ children }: any) => children ? <p className="font-serif-display text-[1em] italic" style={{ fontFamily: "var(--heading-font)", color: "var(--page-muted)" }}>{children}</p> : null;

  if (page.type === "cover") {
    const h = quote.homeowner || {};
    const accent = page.style?.accent_color || "#fbbf24";
    return (
      <div className={`${wrap} relative text-stone-50 ${paper ? "" : "min-h-[85vh]"}`} style={{ ...vars, background: page.style?.bg_color || "#0b0d14" }} data-testid="proposal-page-cover">
        <div className={`${paper ? "paper-inner absolute inset-0" : "relative min-h-[85vh] text-[18px] md:text-[24px]"}`}>
          <img src={img(quote.cover_image_url, "cover")} alt="" className="absolute inset-0 h-full w-full object-cover opacity-85" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#06080f] via-[#06080f]/55 to-[#06080f]/10" />
          <div className="absolute left-[10%] top-[8%] text-[0.65em] uppercase tracking-[0.35em]" style={{ color: accent }}>Starlites Holiday Lighting</div>
          <div className="absolute inset-x-[10%] bottom-[10%]">
            <h1 className="text-[3.6em] font-normal leading-none tracking-tighter" style={{ fontFamily: "var(--heading-font)" }}>{page.title}</h1>
            <p className="mt-[0.6em] text-[0.95em] font-light text-stone-300" style={{ fontFamily: "var(--body-font)" }}>{page.subtitle}</p>
            <div className="mt-[2.2em] flex gap-[3em] text-[0.72em] text-stone-400" style={{ fontFamily: "var(--body-font)" }}>
              <div>Prepared for<b className="block text-[1.15em] font-medium text-stone-50">{h.name}</b></div>
              <div>Property<b className="block text-[1.15em] font-medium text-stone-50">{h.address || "—"}</b></div>
              <div>Proposal<b className="block text-[1.15em] font-medium text-stone-50">v{quote.current_version}</b></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (page.type === "before_after") {
    const before = quote.before_image_url, mock = quote.mockup_url;
    const mocks: any[] = (quote.mockups || []).filter((m: any) => m.url);
    const n = mocks.length;
    const Lbl = ({ children, accent }: any) => <div className="mt-[0.5em] truncate text-[0.6em] uppercase tracking-[0.25em]" style={{ color: accent ? "var(--accent)" : "var(--page-muted)" }}>{children}</div>;
    const Mock = ({ m, aspect }: { m: any; aspect: string }) => (
      <div data-testid={`ba-mockup-${m.id}`}>
        <img src={img(m.url)} alt="" className={`${aspect} w-full rounded object-cover ${m.selected ? "ring-2" : ""}`} style={{ ["--tw-ring-color" as any]: "var(--accent)" }} />
        <Lbl accent={m.selected}>{mockupLabel(m)}{m.selected ? " · Recommended" : ""}</Lbl>
      </div>
    );
    return (
      <div className={wrap} style={pageStyle} data-testid="proposal-page-before-after">
        <div className={`${inner} ${pad} font-proposal`}>
          <Eyebrow>Design Preview</Eyebrow>
          <H className="text-[2.6em] leading-tight tracking-tight">{page.title}</H>
          <Sub>{page.subtitle}</Sub>
          {before && n === 1 ? (
            <div className="mt-[1.2em] space-y-[1em]">
              <div><img src={img(before, "before_after")} alt="Before" className="aspect-[2/1] w-full rounded object-cover" /><Lbl>Before</Lbl></div>
              <Mock m={mocks[0]} aspect="aspect-[2/1]" />
            </div>
          ) : before && n >= 2 ? (
            <div className="mt-[1.2em] space-y-[1em]" data-testid="ba-multi">
              <div><img src={img(before, "before_after")} alt="Before" className={`${n > 2 ? "aspect-[21/8]" : "aspect-[21/9]"} w-full rounded object-cover`} /><Lbl>Before</Lbl></div>
              <div className={`grid gap-[0.9em] ${n > 4 ? "grid-cols-3" : "grid-cols-2"}`}>{mocks.map((m) => <Mock key={m.id} m={m} aspect={n > 4 ? "aspect-[4/3]" : n > 2 ? "aspect-[16/10]" : "aspect-[4/3]"} />)}</div>
            </div>
          ) : (
            <div className="mt-[1.5em]"><img src={img(mock || before || page.image_url, "before_after")} alt="" className="aspect-[16/10] w-full rounded object-cover" /><Lbl>{mock ? "Your Starlites Design" : before ? "Your Home" : "Design preview coming soon"}</Lbl></div>
          )}
          <Body page={page} className="mt-[1.2em] text-[0.8em] leading-relaxed" />
        </div>
      </div>
    );
  }

  if (page.type === "pricing") {
    return (
      <div className={wrap} style={pageStyle} data-testid="proposal-page-pricing">
        <div className={`${inner} ${pad} font-proposal`}>
          <Eyebrow>Investment</Eyebrow>
          <H className="text-[2.6em] leading-tight tracking-tight">{page.title}</H>
          <div className="mb-[1.5em]"><Sub>{page.subtitle}</Sub></div>
          <PricingTable items={quote.line_items || []} pricing={quote.pricing} acceptance={quote.acceptance} />
          <div className="mt-[1.8em] text-[0.7em] leading-relaxed" style={{ color: "var(--page-muted)" }}><Body page={page} />{quote.valid_until ? <p>Valid until {quote.valid_until}</p> : null}</div>
        </div>
      </div>
    );
  }

  const layout = page.layout || "image_right";
  if (layout === "components") {
    const comps = page.components || [];
    const cols = comps.length <= 4 ? "grid-cols-2" : "grid-cols-3";
    return (
      <div className={wrap} style={pageStyle} data-testid="proposal-page-components">
        <div className={`${inner} ${pad} font-proposal`}>
          <Eyebrow>Starlites</Eyebrow>
          <H className="text-[2.6em] leading-[1.05] tracking-tight">{page.title}</H>
          {page.subtitle && <div className="mt-[0.3em]"><Sub>{page.subtitle}</Sub></div>}
          <Body page={page} className="mt-[0.8em] max-w-[85%] text-[0.8em] leading-relaxed" />
          <div className={`mt-[1.6em] grid ${cols} gap-x-[1.2em] gap-y-[1.8em]`}>
            {comps.map((c, i) => (
              <div key={c.id || i} data-testid={`component-card-${i}`}>
                <img src={img(c.image_url, "components")} alt={c.name} className={`${comps.length <= 4 ? "aspect-[16/10]" : "aspect-[5/4]"} w-full rounded object-cover`} />
                <div className="mt-[0.7em] flex items-baseline gap-[0.5em]"><span className="text-[0.55em] font-semibold tabular-nums" style={{ color: "var(--accent)" }}>{String(i + 1).padStart(2, "0")}</span><span className="text-[0.9em] font-semibold leading-tight">{c.name}</span></div>
                {c.description && <p className="mt-[0.35em] text-[0.72em] leading-relaxed" style={{ color: "var(--page-muted)" }}>{c.description}</p>}
              </div>
            ))}
          </div>
          {comps.length === 0 && <p className="mt-[1.5em] text-[0.8em]" style={{ color: "var(--page-muted)" }}>Add components in the Content tab.</p>}
        </div>
      </div>
    );
  }
  const imageUrl = page.image_url;
  const textOnly = layout === "text_only" || (!imageUrl && !fb.content);
  return (
    <div className={wrap} style={pageStyle} data-testid="proposal-page-content">
      <div className={`${inner} ${pad} font-proposal ${paper ? "h-full" : ""} grid ${textOnly ? "grid-cols-1" : paper ? "grid-cols-2" : "grid-cols-1 md:grid-cols-2"} items-start gap-[2.5em]`}>
        {!textOnly && layout === "image_left" && <img src={img(imageUrl, "content")} alt="" className={`${paper ? "h-full" : "aspect-[3/4]"} w-full rounded object-cover`} />}
        <div className={textOnly && !paper ? "max-w-3xl" : ""}>
          <Eyebrow>Starlites</Eyebrow>
          <H className="text-[2.6em] leading-[1.05] tracking-tight">{page.title}</H>
          {page.subtitle && <div className="mt-[0.3em]"><Sub>{page.subtitle}</Sub></div>}
          <Body page={page} className="mt-[1.2em] text-[0.82em] leading-relaxed" />
          {page.bullets?.length ? (
            <ul className="mt-[1em]">
              {page.bullets.map((b, i) => (
                <li key={i} className="relative py-[0.6em] pl-[1.6em] text-[0.82em] leading-relaxed" style={{ borderBottom: "1px solid var(--page-border)" }}>
                  <span className="absolute left-0 top-[1em] h-[0.6em] w-[0.6em] rounded-full" style={{ background: "var(--accent)" }} />{b}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {!textOnly && layout !== "image_left" && <img src={img(imageUrl, "content")} alt="" className={`${paper ? "h-full" : "aspect-[3/4]"} w-full rounded object-cover`} />}
      </div>
    </div>
  );
}
